import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SlikLoader from './branding/SlikLoader';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// The staff app is for shop staff only. Owners resolve to 'owner' (AuthContext honors isOwner), managers
// to 'manager', the station to 'staff', barbers to 'barber' — verified against live roles (Jose 2026-10-12).
// A customer who logs into the staff app, or a stray self-signup with no role, is NOT a staff account.
const ALLOWED_STAFF_ROLES = ['owner', 'admin', 'manager', 'staff', 'barber'];

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading, profileError, logout } = useAuth();

  // Branded full-screen splash while resolving auth — visually identical to the instant
  // pre-React splash (public/index.html), so the cold-boot hand-off is seamless.
  if (loading) {
    return <SlikLoader fullScreen />;
  }

  // Authenticated, but the profile read hung/failed (flaky connection). Never trap the user on an
  // infinite spinner or bounce a valid session to login — offer an explicit retry. (Jose 2026-09-24)
  if (profileError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-sm px-6">
          <p className="text-gray-900 font-semibold mb-1">Connection problem</p>
          <p className="text-gray-500 text-sm mb-5">We couldn't load your profile — usually a slow or dropped connection.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 rounded-xl bg-purple-600 text-white font-medium touch-manipulation active:scale-95"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Authenticated, but NOT a shop-staff account — a customer who signed into the staff app, or a stray
  // self-signup with no role (the gap Diana fell through 2026-10-12). Don't drop them into a bare shell;
  // tell them clearly + give a clean escape (never trap a session). No legit staff user hits this.
  if (!ALLOWED_STAFF_ROLES.includes(((user as any).role || ''))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-sm px-6">
          <p className="text-gray-900 font-semibold mb-1">This isn't a staff login</p>
          <p className="text-gray-500 text-sm mb-5">
            This app is for SLIK staff. If you're a barber, ask the owner to text you an
            <span className="font-medium"> activation link</span>, then tap your PIN. Customers should use the booking app.
          </p>
          <button
            onClick={() => logout()}
            className="px-6 py-2.5 rounded-xl bg-purple-600 text-white font-medium touch-manipulation active:scale-95"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
}
